import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { rtlFormComponentOverrides } from './rtlFormStyles';
import DataEntryForm from './components/DataEntryForm';
import DataDisplay from './components/DataDisplay';
// Removed full-page login usage; use Landing + LoginModal instead
import ModernDashboard from './components/ModernDashboard';
import Layout from './components/Layout';
import Landing from './components/Landing';
import ManageList from './components/ManageList';
import CompanyReports from './components/CompanyReports';

const theme = createTheme({
  direction: 'rtl',
  typography: {
    fontFamily: 'Vazirmatn, Arial, sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 700,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
    },
  },
  palette: {
    primary: {
      main: '#00a86a',
      light: '#00c875',
      dark: '#037f4c',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#0073ea',
      light: '#cce5ff',
      dark: '#0060c2',
      contrastText: '#ffffff',
    },
    error: { main: '#e44258', dark: '#c9364a' },
    warning: { main: '#fdab3d' },
    success: { main: '#00c875' },
    info: { main: '#0073ea' },
    text: {
      primary: '#323338',
      secondary: '#676879',
      disabled: '#c5c7d0',
    },
    background: {
      default: '#f7f8fa',
      paper: '#ffffff',
    },
    divider: '#e6e9ef',
  },
  components: {
    ...rtlFormComponentOverrides,
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 12,
          fontWeight: 600,
          padding: '8px 24px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 20px rgba(50, 51, 56, 0.08)',
          border: '1px solid #e6e9ef',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          ...rtlFormComponentOverrides.MuiTextField.styleOverrides.root,
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
          },
        },
      },
    },
  },
});

const PrivateRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  return token ? <Layout>{children}</Layout> : <Navigate to="/login" />;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          {/* New landing page as root */}
          <Route path="/" element={<Landing />} />
          
          {/* Route /login opens landing with login modal open */}
          <Route path="/login" element={<Landing defaultOpenLogin />} />
          
          {/* Admin dashboard route */}
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <ModernDashboard />
              </PrivateRoute>
            }
          />
          
          {/* Protected routes */}
          <Route
            path="/entry"
            element={
              <PrivateRoute>
                <DataEntryForm />
              </PrivateRoute>
            }
          />
          <Route
            path="/manage/:entity"
            element={
              <PrivateRoute>
                <ManageList />
              </PrivateRoute>
            }
          />
          <Route
            path="/display"
            element={
              <PrivateRoute>
                <DataDisplay />
              </PrivateRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <PrivateRoute>
                <CompanyReports />
              </PrivateRoute>
            }
          />
          
          {/* Catch all other routes and redirect to root */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
